import React, { useState, useContext } from 'react';
import { AppContext } from '../context/AppContext';
import '../../src/assets/css/SalaryStructure.css';

// --- Hardcoded Dictionaries ---
const professionalTaxSlabs = {
  "Maharashtra": { limit1: 7500, tax1: 0, limit2: 10000, tax2: 175, limit3: Infinity, tax3: 200 },
  "Karnataka": { limit1: 24999, tax1: 0, limit2: Infinity, tax2: 200 },
  "West Bengal": { limit1: 10000, tax1: 0, limit2: 15000, tax2: 110, limit3: 25000, tax3: 130, limit4: 40000, tax4: 150, limit5: Infinity, tax5: 200 },
  "Tamil Nadu": { limit1: 21000, tax1: 0, limit2: Infinity, tax2: 200 },
  "Delhi": { limit1: Infinity, tax1: 0 },
  "Haryana": { limit1: Infinity, tax1: 0 },
  "Others": { limit1: Infinity, tax1: 0 }
};

const lwfSlabs = {
  "Haryana": { employee: 34, employer: 68 },
  "Karnataka": { employee: 4.16, employer: 8.33 },
  "Maharashtra": { employee: 4.16, employer: 12.5 },
  "Tamil Nadu": { employee: 1.66, employer: 3.33 },
  "Delhi": { employee: 0.12, employer: 0.37 },
  "Others": { employee: 0, employer: 0 }
};

const TermTooltip = ({ title, text, position = 'bottom' }) => {
  const [show, setShow] = useState(false);
  const posStyle = position === 'bottom' ? { top: '120%', left: '0' } : { bottom: '120%', left: '0' };
  return (
    <span
      className="position-relative"
      style={{ textDecoration: 'underline dotted', cursor: 'help', color: '#53b7e8' }}
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
    >
      {title}
      {show && (
        <div
          className="position-absolute text-white p-3 rounded shadow-lg text-start"
          style={{ ...posStyle, width: '320px', zIndex: 1050, fontSize: '0.8rem', pointerEvents: 'none', fontWeight: 'normal', lineHeight: '1.5', backgroundColor: '#1b2b3b' }}
        >
          {text}
        </div>
      )}
    </span>
  );
};

export default function SalaryCalculator() {
  const { sidebar } = useContext(AppContext);

  // --- Section A States ---
  const [ctc, setCtc] = useState(1200000);
  const [workState, setWorkState] = useState("Maharashtra");
  const [cityType, setCityType] = useState("Metro");
  const [age, setAge] = useState(30);
  const [isDisabled, setIsDisabled] = useState(false);
  const [pfPolicy, setPfPolicy] = useState("Cap at 15000");
  const [statutoryBonusPct, setStatutoryBonusPct] = useState(8.33);

  // --- Section B States (Annual Values) ---
  const [bonus, setBonus] = useState(0);
  const [esop, setEsop] = useState(0);
  const [giftVoucher, setGiftVoucher] = useState(0);
  const [mealVoucher, setMealVoucher] = useState(0);
  const [flexFund, setFlexFund] = useState(0);

  // --- Core Business Logic ---
  const totalRewardsValue = Number(bonus) + Number(esop) + Number(giftVoucher) + Number(mealVoucher) + Number(flexFund);
  const coreAnnualCtc = Math.max(0, Number(ctc) - totalRewardsValue);
  const targetMonthlyCoreCtc = coreAnnualCtc / 12;

  const basic = targetMonthlyCoreCtc * 0.5;

  let employerPF = 0;
  if (pfPolicy === "Cap at 15000") {
    employerPF = basic > 15000 ? 1800 : basic * 0.12;
  } else {
    employerPF = basic * 0.12;
  }
  const edli = Math.min(basic * 0.01, 150);
  const gratuity = basic * 0.0481;
  const employerLWF = lwfSlabs[workState]?.employer || 0;

  let statutoryBonusMonthly = 0;
  if (basic <= 21000) {
    const safePct = Math.min(20, Math.max(8.33, Number(statutoryBonusPct)));
    statutoryBonusMonthly = basic * (safePct / 100);
  }

  let grossSalary = targetMonthlyCoreCtc - (employerPF + edli + gratuity + employerLWF + statutoryBonusMonthly);
  const esiLimit = isDisabled ? 25000 : 21000;
  let employerESI = 0;
  let employeeESI = 0;
  if (grossSalary <= esiLimit) {
    employerESI = grossSalary * 0.0325;
    employeeESI = grossSalary * 0.0075;
    grossSalary = grossSalary - employerESI;
  }

  const hra = cityType === "Metro" ? basic * 0.5 : basic * 0.4;
  const specialAllowance = Math.max(0, grossSalary - (basic + hra));

  const employeePF = employerPF;
  const employeeLWF = lwfSlabs[workState]?.employee || 0;

  let pt = 0;
  if (workState === "Maharashtra" && age >= 65) {
    pt = 0;
  } else {
    const statePT = professionalTaxSlabs[workState] || professionalTaxSlabs["Others"];
    if (grossSalary <= statePT.limit1) pt = statePT.tax1;
    else if (statePT.limit2 && grossSalary <= statePT.limit2) pt = statePT.tax2;
    else if (statePT.limit3 && grossSalary <= statePT.limit3) pt = statePT.tax3;
    else if (statePT.limit4 && grossSalary <= statePT.limit4) pt = statePT.tax4;
    else if (statePT.limit5 && grossSalary <= statePT.limit5) pt = statePT.tax5;
    else pt = statePT.tax2 || 0;
  }

  let taxableRewards = totalRewardsValue;
  let annualIncome = (grossSalary * 12) + taxableRewards;
  let stdDeduction = 75000;
  let netTaxable = Math.max(0, annualIncome - stdDeduction);
  let annualTax = 0;
  if (netTaxable > 2400000) annualTax += (netTaxable - 2400000) * 0.30 + 300000;
  else if (netTaxable > 2000000) annualTax += (netTaxable - 2000000) * 0.25 + 200000;
  else if (netTaxable > 1600000) annualTax += (netTaxable - 1600000) * 0.20 + 120000;
  else if (netTaxable > 1200000) annualTax += (netTaxable - 1200000) * 0.15 + 60000;
  else if (netTaxable > 800000) annualTax += (netTaxable - 800000) * 0.10 + 20000;
  else if (netTaxable > 400000) annualTax += (netTaxable - 400000) * 0.05;
  if (netTaxable <= 1200000) annualTax = 0;
  annualTax = annualTax * 1.04;
  const monthlyTDS = annualTax / 12;

  const netSalary = grossSalary - (employeePF + employeeESI + pt + employeeLWF + monthlyTDS);

  const hypotheticalExtraBasic = (totalRewardsValue / 12) * 0.5;
  const savedStatutory = hypotheticalExtraBasic * (0.12 + 0.0481);

  return (
    <div className={sidebar ? "taskMain" : "cx-active taskMain"} id="cx-main">
      <div className="role-content">

        {/* Page Header */}
        <div className="sub-header-role">
          <h6 className="mt-2">2026 Labour Code Salary Calculator</h6>
        </div>

        <div className="innerRoleSec">
          <div className="row">

            {/* ── LEFT COLUMN: Inputs ── */}
            <div className="col-lg-6 col-md-12 mb-4">

              {/* Section A */}
              <div className="card border-0 shadow-sm mb-4">
                <div className="card-header text-white border-0" style={{ backgroundColor: '#53b7e8' }}>
                  <h6 className="mb-0 fw-semibold">Section A: Core Payroll Inputs</h6>
                </div>
                <div className="card-body">

                  <div className="mb-3">
                    <label className="enter-role-text mb-1">Annual CTC (₹)</label>
                    <input
                      type="number"
                      className="form-control tasKCategory1"
                      value={ctc}
                      onChange={(e) => setCtc(e.target.value)}
                    />
                  </div>

                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label className="enter-role-text mb-1">Work State</label>
                      <select className="form-select tasKCategory1" value={workState} onChange={(e) => setWorkState(e.target.value)}>
                        {Object.keys(professionalTaxSlabs).map(state => (
                          <option key={state} value={state}>{state}</option>
                        ))}
                      </select>
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="enter-role-text mb-1">City Type</label>
                      <select className="form-select tasKCategory1" value={cityType} onChange={(e) => setCityType(e.target.value)}>
                        <option value="Metro">Metro</option>
                        <option value="Non-Metro">Non-Metro</option>
                      </select>
                    </div>
                  </div>

                  <div className="mb-3">
                    <label className="enter-role-text mb-1">Age</label>
                    <input
                      type="number"
                      className="form-control tasKCategory1"
                      value={age}
                      onChange={(e) => setAge(e.target.value)}
                    />
                    <small className="text-muted" style={{ fontSize: '0.78rem' }}>
                      Required to determine EPS eligibility (stops at 58) and state-specific PT exemptions.
                    </small>
                  </div>

                  <div className="mb-3 form-check">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="pwdCheck"
                      checked={isDisabled}
                      onChange={(e) => setIsDisabled(e.target.checked)}
                    />
                    <label className="form-check-label text-muted" htmlFor="pwdCheck">
                      Person with Disability (Impacts ESI limit)
                    </label>
                  </div>

                  <div className="mb-3">
                    <label className="enter-role-text mb-1">Employer PF Policy</label>
                    <select className="form-select tasKCategory1" value={pfPolicy} onChange={(e) => setPfPolicy(e.target.value)}>
                      <option value="Cap at 15000">Cap at statutory wage limit of ₹15,000</option>
                      <option value="Full Basic">Calculate on full Basic</option>
                    </select>
                  </div>

                  <div className="mb-3">
                    <label className="enter-role-text mb-1">Statutory Bonus Payout (%)</label>
                    <div className="input-group">
                      <input
                        type="number"
                        className="form-control tasKCategory1"
                        value={statutoryBonusPct}
                        onChange={(e) => setStatutoryBonusPct(e.target.value)}
                        min="8.33"
                        max="20"
                        step="0.01"
                      />
                      <span className="input-group-text">%</span>
                    </div>
                    <small className="text-muted" style={{ fontSize: '0.78rem' }}>
                      Legally required for wages ≤ ₹21,000/mo. Min 8.33%, Max 20%.
                    </small>
                  </div>

                </div>
              </div>

              {/* Section B */}
              <div className="card border-0 shadow-sm">
                <div className="card-header border-0" style={{ backgroundColor: '#e8f7fd' }}>
                  <h6 className="mb-0 fw-semibold" style={{ color: '#53b7e8' }}>Section B: Tax-Efficient Rewards (Annual)</h6>
                </div>
                <div className="card-body">
                  <p className="text-muted small mb-4">
                    Amounts here are excluded from the 50% Rule calculation, preventing inflated statutory costs.
                  </p>

                  {[
                    { label: 'Performance-Linked Bonuses (₹)', value: bonus, setter: setBonus, badge: 'secondary', badgeText: 'No cap (Fully Taxable)' },
                    { label: 'Employee Stock Ownership (ESOPs) (₹)', value: esop, setter: setEsop, badge: 'secondary', badgeText: 'No max cap (Taxable at exercise)' },
                    { label: 'Gift & Festival Vouchers (₹)', value: giftVoucher, setter: setGiftVoucher, badge: 'success', badgeText: 'Tax-free up to ₹5,000/year' },
                    { label: 'Meal Vouchers / Digital Allowances (₹)', value: mealVoucher, setter: setMealVoucher, badge: 'success', badgeText: 'Tax-free up to ₹26,400/year' },
                    { label: 'Flexible Wellness & Learning Funds (₹)', value: flexFund, setter: setFlexFund, badge: 'secondary', badgeText: 'No cap (Fully Taxable)' },
                  ].map(({ label, value, setter, badge, badgeText }) => (
                    <div className="mb-4" key={label}>
                      <label className="enter-role-text mb-1">{label}</label>
                      <input
                        type="number"
                        className="form-control tasKCategory1"
                        value={value}
                        onChange={(e) => setter(e.target.value)}
                      />
                      <div className="mt-1">
                        <span className={`badge bg-${badge} me-1`}>Limit</span>
                        <span className="small fw-semibold text-dark">{badgeText}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

            </div>

            {/* ── RIGHT COLUMN: Outputs ── */}
            <div className="col-lg-6 col-md-12">

              {/* Card A: Employee Take-Home */}
              <div className="card border-0 shadow-sm mb-4">
                <div className="card-header text-white border-0" style={{ backgroundColor: '#28a745' }}>
                  <h6 className="mb-0 fw-semibold">Card A: Employee's Monthly Net Take-Home</h6>
                </div>
                <div className="card-body">
                  <table className="w-100 mb-3" style={{ fontSize: '0.9rem' }}>
                    <tbody>
                      <tr>
                        <td className="py-1 text-muted"><TermTooltip title="Basic Salary" text="Core component. Must be ≥ 50% of total core remuneration under 2026 Labour Codes." /></td>
                        <td className="py-1 text-end fw-semibold">₹{basic.toFixed(0)}</td>
                      </tr>
                      <tr>
                        <td className="py-1 text-muted"><TermTooltip title="HRA" text="50% of Basic for Metro, 40% for Non-Metro." /></td>
                        <td className="py-1 text-end fw-semibold">₹{hra.toFixed(0)}</td>
                      </tr>
                      <tr className="border-bottom">
                        <td className="py-1 text-muted"><TermTooltip title="Special Allowance" text="Residual balancing figure after all components are accounted for. Generally fully taxable." position="bottom" /></td>
                        <td className="py-1 text-end fw-semibold">₹{specialAllowance.toFixed(0)}</td>
                      </tr>
                      <tr>
                        <td className="py-2 fw-bold"><TermTooltip title="Total Gross Salary" text="Total monthly payout before employee deductions or TDS." position="bottom" /></td>
                        <td className="py-2 text-end fw-bold">₹{grossSalary.toFixed(0)}</td>
                      </tr>
                    </tbody>
                  </table>

                  <p className="fw-semibold mb-2" style={{ color: '#dc3545', borderBottom: '1px solid #dee2e6', paddingBottom: '6px' }}>Deductions</p>
                  <table className="w-100 mb-3" style={{ fontSize: '0.85rem' }}>
                    <tbody>
                      <tr><td className="py-1 text-muted"><TermTooltip title="Employee PF" text="12% contribution towards retirement corpus." position="top" /></td><td className="py-1 text-end">₹{employeePF.toFixed(0)}</td></tr>
                      <tr><td className="py-1 text-muted"><TermTooltip title="Employee ESI" text="0.75% health insurance for gross ≤ ₹21,000/mo." position="top" /></td><td className="py-1 text-end">₹{employeeESI.toFixed(0)}</td></tr>
                      <tr><td className="py-1 text-muted"><TermTooltip title="Professional Tax" text="State-level tax. Slabs vary by Work State." position="top" /></td><td className="py-1 text-end">₹{pt.toFixed(0)}</td></tr>
                      <tr><td className="py-1 text-muted"><TermTooltip title="Employee LWF" text="Nominal state-specific Labour Welfare Fund contribution." position="top" /></td><td className="py-1 text-end">₹{employeeLWF.toFixed(0)}</td></tr>
                      <tr><td className="py-1 fw-semibold text-muted"><TermTooltip title="Monthly TDS" text="Projected monthly income tax under New Tax Regime." position="top" /></td><td className="py-1 text-end fw-semibold">₹{monthlyTDS.toFixed(0)}</td></tr>
                    </tbody>
                  </table>

                  <div className="p-3 rounded" style={{ backgroundColor: '#d1e7dd', border: '1px solid #badbcc' }}>
                    <div className="d-flex justify-content-between align-items-center">
                      <h6 className="text-success mb-0 fw-bold">NET TAKE-HOME</h6>
                      <h5 className="text-success mb-0 fw-bold">₹{netSalary.toFixed(0)}</h5>
                    </div>
                  </div>

                  {statutoryBonusMonthly > 0 && (
                    <div className="mt-3 p-2 rounded text-center" style={{ backgroundColor: '#e2e3e5', fontSize: '0.82rem' }}>
                      <span className="fw-semibold">Annual Benefit:</span> Mandatory Statutory Bonus of{' '}
                      <strong>₹{(statutoryBonusMonthly * 12).toFixed(0)}</strong> paid at year end.
                    </div>
                  )}
                </div>
              </div>

              {/* Card B: Employer CTC */}
              <div className="card border-0 shadow-sm mb-4">
                <div className="card-header text-white border-0" style={{ backgroundColor: '#6c757d' }}>
                  <h6 className="mb-0 fw-semibold">Card B: Employer's Cost to Company</h6>
                </div>
                <div className="card-body">
                  <table className="w-100" style={{ fontSize: '0.88rem' }}>
                    <tbody>
                      <tr>
                        <td className="py-1 text-muted">Gross Salary Paid</td>
                        <td className="py-1 text-end">₹{grossSalary.toFixed(0)}</td>
                      </tr>
                    </tbody>
                  </table>
                  <p className="fw-semibold text-muted mt-3 mb-2" style={{ borderBottom: '1px solid #dee2e6', paddingBottom: '6px', fontSize: '0.85rem' }}>
                    Employer Statutory Costs
                  </p>
                  <table className="w-100" style={{ fontSize: '0.85rem' }}>
                    <tbody>
                      <tr><td className="py-1"><TermTooltip title="Employer PF" text="Company's matching 12% retirement contribution." position="top" /></td><td className="py-1 text-end">₹{employerPF.toFixed(0)}</td></tr>
                      <tr><td className="py-1"><TermTooltip title="Employer ESI" text="Company's 3.25% contribution to ESI scheme." position="top" /></td><td className="py-1 text-end">₹{employerESI.toFixed(0)}</td></tr>
                      <tr><td className="py-1"><TermTooltip title="EDLI & Admin" text="0.5% employer-funded life insurance for EPF members." position="top" /></td><td className="py-1 text-end">₹{edli.toFixed(0)}</td></tr>
                      <tr><td className="py-1"><TermTooltip title="Gratuity Provision" text="~4.81% of Basic set aside monthly. Payable after 5 years." position="top" /></td><td className="py-1 text-end">₹{gratuity.toFixed(0)}</td></tr>
                      <tr><td className="py-1"><TermTooltip title="Employer LWF" text="Company's share of state Labour Welfare Fund." position="top" /></td><td className="py-1 text-end">₹{employerLWF.toFixed(0)}</td></tr>
                      {statutoryBonusMonthly > 0 && (
                        <tr><td className="py-1"><TermTooltip title="Statutory Bonus" text="Mandatory for companies with 20+ employees if wages ≤ ₹21,000/mo." position="top" /></td><td className="py-1 text-end">₹{statutoryBonusMonthly.toFixed(0)}</td></tr>
                      )}
                    </tbody>
                  </table>
                  <div className="d-flex justify-content-between align-items-center mt-3 p-2 rounded" style={{ backgroundColor: '#f8f9fa', border: '1px solid #dee2e6' }}>
                    <span className="fw-bold" style={{ fontSize: '0.9rem' }}>
                      <TermTooltip title="CORE MONTHLY CTC" text="Total monthly employer cost including gross salary and all statutory contributions." position="top" />
                    </span>
                    <span className="fw-bold">₹{targetMonthlyCoreCtc.toFixed(0)}</span>
                  </div>
                </div>
              </div>

              {/* Card C: Tax-Efficient Rewards */}
              {totalRewardsValue > 0 && (
                <div className="card border-0 shadow-sm" style={{ borderLeft: '4px solid #ffc107' }}>
                  <div className="card-header border-0" style={{ backgroundColor: '#fff3cd' }}>
                    <h6 className="mb-0 fw-semibold text-dark">Card C: Value of Tax-Efficient Rewards</h6>
                  </div>
                  <div className="card-body">
                    <p className="small text-muted mb-3">Monthly breakdown of optional rewards (excluded from statutory overheads):</p>
                    <table className="w-100" style={{ fontSize: '0.88rem' }}>
                      <tbody>
                        {bonus > 0 && <tr><td className="py-1 fw-semibold">Bonus</td><td className="py-1 text-end">₹{(bonus / 12).toFixed(0)}/mo</td></tr>}
                        {esop > 0 && <tr><td className="py-1 fw-semibold">ESOPs</td><td className="py-1 text-end">₹{(esop / 12).toFixed(0)}/mo</td></tr>}
                        {giftVoucher > 0 && <tr><td className="py-1 fw-semibold">Gift Vouchers</td><td className="py-1 text-end">₹{(giftVoucher / 12).toFixed(0)}/mo</td></tr>}
                        {mealVoucher > 0 && <tr><td className="py-1 fw-semibold">Meal Vouchers</td><td className="py-1 text-end">₹{(mealVoucher / 12).toFixed(0)}/mo</td></tr>}
                        {flexFund > 0 && <tr><td className="py-1 fw-semibold">Flex Funds</td><td className="py-1 text-end">₹{(flexFund / 12).toFixed(0)}/mo</td></tr>}
                      </tbody>
                    </table>
                    <div className="mt-3 p-3 rounded" style={{ backgroundColor: '#fff3cd', color: '#856404', border: '1px solid #ffeeba', fontSize: '0.85rem' }}>
                      <strong>Insight:</strong> By structuring these as performance payouts, you bypassed the 50% Wage Rule — saving approx{' '}
                      <strong>₹{savedStatutory.toFixed(0)}</strong>/mo in inflated PF & Gratuity costs.
                    </div>
                  </div>
                </div>
              )}

            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
